import { useState, useEffect } from "react";
import {
  ArrowRight,
  MessageSquare,
  Phone,
  Check,
  ChevronUp,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import PulsatingGlowTag from "@/components/ui/PulsatingGlowTag";

const NPOlandingUS = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <SEO
        title="Your 24/7 Nonprofit Assistant | CORAA by CauseConnect"
        description="Nonprofits miss out on corporate support because they can't reply fast enough. CORAA changes that. It chats, books, and follows up, while you focus on the real work."
      />
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 px-6 overflow-hidden">
          <div className="max-w-5xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="block">Your Website Is Open 24/7.</span>
              <span className="text-[#2D7D46]">
                Now It Can Actually Respond.
              </span>
            </motion.h1>

            <motion.p
              className="text-xl text-gray-600 max-w-3xl mx-auto mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Nonprofits miss out on corporate support because they can't reply fast enough. 
              CORAA changes that. It chats, calls, books, and follows up, while you focus on the real work.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row justify-center gap-4 mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Button
                onClick={() =>
                  window.open(
                    "https://calendly.com/vijay-causeconnect/30min?a1=Interested%20in%20demo%20as%20a%20Non-profit",
                    "_blank"
                  )
                }
                className="bg-[#2D7D46] hover:bg-[#256d3b] text-white px-8 py-6 text-lg rounded-full font-medium transition-colors flex items-center gap-2"
              >
                Book a Demo
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                className="px-8 py-6 text-lg rounded-full border-2 border-gray-200 hover:border-[#2D7D46] hover:bg-transparent text-gray-700 font-medium flex items-center gap-2"
                onClick={() => {
                  const element = document.getElementById("chat-simulation");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                <Play className="h-5 w-5 text-[#2D7D46]" />
                See It Work
              </Button>
            </motion.div>

            <p className="text-gray-500 text-sm">
              ✨ CORAA Chat is free. CORAA Voice is just $20/month — the cost of
              a single Uber ride.
            </p>
          </div>

          <div className="absolute -z-10 top-0 right-0 w-1/2 h-full bg-gradient-to-br from-[#2D7D46]/5 to-transparent rounded-full"></div>
        </section>

        {/* Chat Section */}
        <section id="chat" className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <PulsatingGlowTag className="mb-4">CORAA CHAT</PulsatingGlowTag>
              <h2 className="text-4xl font-bold mb-6">Turn Interest Into Action — <span className="text-[#2D7D46]">Without Lifting a Finger</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                CORAA Chat is your 24/7 teammate. It lives on your website, speaks like a human, and helps people take real steps, from booking a volunteering event to asking about your programs.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  icon: <MessageSquare className="h-8 w-8 text-[#2D7D46]" />,
                  title: "Multilingual Support",
                  description: "Answers instantly in 50+ languages",
                },
                {
                  icon: <Check className="h-8 w-8 text-[#2D7D46]" />,
                  title: "Trained on Your Content",
                  description: "Knows your offerings, impact areas, and FAQs",
                },
                {
                  icon: <MessageSquare className="h-8 w-8 text-[#2D7D46]" />,
                  title: "Seamless Integration",
                  description:
                    "Available as a WordPress plugin or simple embed",
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#2D7D46]/10 flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <p className="text-lg text-gray-700">
                <span className="font-medium">Forever free.</span> Because missed opportunities shouldn't be normal.
              </p>
            </div>
          </div>
        </section>

        {/* Voice Section */}
        <section id="voice" className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <PulsatingGlowTag className="mb-4">CORAA VOICE</PulsatingGlowTag>
              <h2 className="text-4xl font-bold mb-6">Your Own Dedicated Number. <span className="text-[#2D7D46]">Your AI Teammate Answers.</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                CORAA Voice picks up the phone for you — literally.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-gray-50 p-8 rounded-2xl">
                  <div className="bg-black rounded-2xl p-6 relative h-96">
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-lg"></div>
                    <div className="h-full flex flex-col">
                      <div className="text-white text-center py-4">
                        <div className="w-16 h-16 rounded-full bg-[#2D7D46] mx-auto flex items-center justify-center mb-3">
                          <Phone className="h-6 w-6 text-white" />
                        </div>
                        <p className="text-sm text-gray-300">Incoming Call</p>
                        <p className="text-xl font-medium">(555) 123-4567</p>
                      </div>
                      <div className="mt-auto mb-6 px-4">
                        <div className="bg-gray-800 rounded-lg p-4 text-sm text-gray-300">
                          <p className="font-medium">AI Assistant:</p>
                          <p>"Thanks for calling! How can I help you today?"</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6"
              >
                {[
                  {
                    title: "Dedicated Number",
                    description:
                      "You get a dedicated phone number for your organization",
                  },
                  {
                    title: "Natural Conversations",
                    description:
                      "CORAA answers with a natural, human-like voice",
                  },
                  {
                    title: "Smart Follow-ups",
                    description:
                      "Automatically collects information and sends email summaries",
                  },
                  {
                    title: "24/7 Availability",
                    description:
                      "Never miss an opportunity, even outside business hours",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full bg-[#2D7D46]/10 flex items-center justify-center">
                        <Check className="h-4 w-4 text-[#2D7D46]" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}

                <div className="pt-4">
                  <p className="text-gray-700">
                    <span className="font-medium">Includes:</span> AI voice assistant, email automation, lead capture, calendar triggers
                  </p>
                  <p className="text-lg font-medium text-gray-900 mt-3">
                    Only $20/month — less than two meals, and it pays for itself with one booking.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">
              Simple Plans That{" "}
              <span className="text-[#2D7D46]">Just Make Sense</span>
            </h2>
            <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
              Choose the plan that works for your nonprofit. No hidden fees, no
              surprises.
            </p>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  name: "CORAA Chat",
                  price: "Free",
                  description: "Perfect for nonprofits getting started with AI",
                  features: [
                    "24/7 website assistant",
                    "Lead capture",
                    "Multilingual support",
                    "WordPress plugin",
                  ],
                  cta: (
                    <a
                      href="/signup/nonprofit"
                      className="text-white hover:underline"
                    >
                      Get Started
                    </a>
                  ),
                },
                {
                  name: "CORAA Voice",
                  price: "$20/month",
                  description: "For nonprofits that need phone support too",
                  features: [
                    "Everything in Chat",
                    "Dedicated phone number",
                    "Smart voice replies",
                    "Instant email triggers",
                  ],
                  cta: (
                    <a
                      href="https://calendly.com/vijay-causeconnect/30min?a1=Become%20a%20Verified%20Nonprofit"
                      className="text-white hover:underline"
                    >
                      Start Free Trial
                    </a>
                  ),
                  featured: true,
                },
              ].map((plan, index) => (
                <motion.div
                  key={index}
                  className={`bg-white rounded-2xl p-8 text-left ${
                    plan.featured
                      ? "ring-2 ring-[#2D7D46] relative"
                      : "shadow-sm hover:shadow-md"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  {plan.featured && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#2D7D46] text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  <div className="mb-8">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.price !== "Free" && (
                      <span className="text-gray-500">/month</span>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-[#2D7D46] mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full py-6 text-lg rounded-xl font-medium ${
                      plan.featured
                        ? "bg-[#2D7D46] hover:bg-[#256d3b]"
                        : "bg-gray-900 hover:bg-gray-800"
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row justify-center items-center gap-4 text-gray-600">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-[#2D7D46] mr-2" />
                <span>No contracts</span>
              </div>
              <div className="hidden sm:block">•</div>
              <div className="flex items-center">
                <Check className="h-5 w-5 text-[#2D7D46] mr-2" />
                <span>No setup fees</span>
              </div>
              <div className="hidden sm:block">•</div>
              <div className="flex items-center">
                <Check className="h-5 w-5 text-[#2D7D46] mr-2" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </section>

        {/* Chat Simulation */}
        <section id="chat-simulation" className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6">
                What a <span className="text-[#2D7D46]">Real Conversation</span>{" "}
                Looks Like
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Here's how CORAA handles a corporate HR manager visiting your
                site
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Chat header */}
                <div className="bg-[#2D7D46] p-4 text-white flex items-center">
                  <div className="w-3 h-3 rounded-full bg-white/30 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-white/30 mr-2"></div>
                  <div className="w-3 h-3 rounded-full bg-white/30"></div>
                  <div className="ml-auto text-sm">CORAA Assistant</div>
                </div>

                {/* Chat messages */}
                <div className="p-4 space-y-4 h-96 overflow-y-auto">
                  <div className="flex justify-start">
                    <div className="max-w-xs md:max-w-md bg-gray-100 rounded-2xl rounded-tl-none p-4 text-gray-800">
                      Hi there!  Looking to plan a volunteering day for your team?
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <div className="max-w-xs md:max-w-md bg-[#2D7D46] text-white rounded-2xl rounded-tr-none p-4">
                      Yes! We're a group of 20, based in SF. Looking for
                      something in July.
                    </div>
                  </div>

                  <div className="flex justify-start">
                    <div className="max-w-xs md:max-w-md bg-gray-100 rounded-2xl rounded-tl-none p-4 text-gray-800">
                      Here's a great fit:
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>📦 Book Kit Assembly</li>
                        <li>👥 Up to 25 participants</li>
                        <li>📍 At your office</li>
                        <li>💰 $2,400 (kits, shipping, guidance included)</li>
                        <li>⏰ 2-hour event</li>
                      </ul>
                      <p className="mt-2">Would you like to reserve a slot?</p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <div className="max-w-xs md:max-w-md bg-[#2D7D46] text-white rounded-2xl rounded-tr-none p-4">
                      Yes — July 19, 10:30 AM.
                      <br />
                      Amanda Thompson, FutureWave Inc, amanda@futurewave.com
                    </div>
                  </div>

                  <div className="flex justify-start">
                    <div className="max-w-xs md:max-w-md bg-gray-100 rounded-2xl rounded-tl-none p-4 text-gray-800">
                      ✅ Done! Booking confirmed. You'll get an email now with:
                      <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                        <li>Event overview</li>
                        <li>Internal promo poster</li>
                        <li>Payment link & contact</li>
                        <li>Impact kit template</li>
                      </ul>
                      <p className="mt-2">
                        Need help with reminders or post-event storytelling?
                        Just ask anytime. 💚
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Get Started Section */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">
              Get Started in{" "}
              <span className="text-[#2D7D46]">3 Easy Steps</span>
            </h2>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  number: "1",
                  title: "Sign Up",
                  description: "Create your free account"
                },
                {
                  number: "2",
                  title: "Set Up",
                  description: "Install plugin or add a short script to your site"
                },
                {
                  number: "3",
                  title: "Go Live",
                  description: "Start interacting with visitors 24/7"
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 rounded-full bg-[#2D7D46]/10 text-[#2D7D46] text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">
                Ready to never miss an opportunity again?
              </h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join thousands of nonprofits already using CORAA to capture more
                leads and save time.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  onClick={() =>
                    window.open(
                      "https://calendly.com/vijay-causeconnect/30min?a1=Interested%20in%20demo%20as%20a%20Non-profit",
                      "_blank"
                    )
                  }
                  className="bg-[#2D7D46] hover:bg-[#256d3b] text-white px-8 py-6 text-lg rounded-full font-medium transition-colors flex items-center gap-2"
                >
                  Book a Demo
                  <ArrowRight className="h-5 w-5" />
                </Button>
                <Button
                  onClick={() =>
                    window.open(
                      "https://calendly.com/vijay-causeconnect/30min?a1=Interested%20in%20demo%20as%20a%20Non-profit",
                      "_blank"
                    )
                  }
                  variant="outline"
                  className="px-8 py-6 text-lg rounded-full border-2 border-gray-200 hover:border-[#2D7D46] hover:bg-transparent text-gray-700 font-medium flex items-center gap-2"
                >
                  Add CORAA to My Site
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                No credit card required • Cancel anytime
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Floating CTA Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <motion.button
          className="bg-[#2D7D46] hover:bg-[#256d3b] text-white p-4 rounded-full shadow-lg flex items-center justify-center transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
        >
          <ChevronUp className="h-6 w-6" />
        </motion.button>
      </div>
    </div>
  );
};

export default NPOlandingUS;
